import { type JSX, useState } from 'react';

import type { BuilderStore } from '../state.js';
import { confirmDialog } from '../../components/dialogs.js';

export interface ProfileBarProps {
  store: BuilderStore;
}

const MAX_PROFILES = 20;

/** Horizontal drop position relative to a target pill. */
type DropPosition = 'left' | 'right';
interface DropTarget {
  id: string;
  position: DropPosition;
}

/**
 * Profile picker strip — sits between the toolbar and the editor body.
 *
 * Every profile is a clickable pill; the active one is highlighted and
 * marked with the 'default' tag when it matches `config.defaultProfileId`.
 * Duplicate/delete affordances sit next to the active pill so they always
 * refer to the profile currently being edited.
 *
 * Delete is gated on profiles.length > 1 (Zod requires ≥1) and prompts
 * before destroying tile work — the action also drops any schedules tied
 * to the removed profile, so a stray Schedule won't survive and trip the
 * root-level cross-field check.
 */
export function ProfileBar({ store }: ProfileBarProps): JSX.Element {
  const { profiles } = store.config;
  const canDelete = profiles.length > 1;
  const canAdd = profiles.length < MAX_PROFILES;
  const canReorder = profiles.length > 1;
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<DropTarget | null>(null);

  function handleDelete(): void {
    const target = store.activeProfile;
    void (async () => {
      const ok = await confirmDialog({
        title: 'Delete profile?',
        message: `Delete profile "${target.name}"? Its tiles, theme, status bar, and any schedules pointing at it will be removed.`,
        confirmLabel: 'Delete profile',
        destructive: true,
        requireText: 'delete',
      });
      if (!ok) return;
      store.removeProfile(target.id);
    })();
  }

  function handleDrop(): void {
    if (draggingId !== null && dropTarget !== null) {
      const toIdx = profiles.findIndex((p) => p.id === dropTarget.id);
      if (toIdx !== -1) {
        const insertAt = dropTarget.position === 'left' ? toIdx : toIdx + 1;
        store.moveProfileTo(draggingId, insertAt);
      }
    }
    setDraggingId(null);
    setDropTarget(null);
  }

  return (
    <div className="profile-bar" role="toolbar" aria-label="Profiles">
      <span className="profile-bar__label">Profile</span>

      <div
        className="profile-bar__pills"
        role="tablist"
        aria-label="Switch active profile"
        onDragEnd={() => {
          setDraggingId(null);
          setDropTarget(null);
        }}
      >
        {profiles.map((p) => {
          const active = p.id === store.activeProfileId;
          const isDefault = p.id === store.config.defaultProfileId;
          const isDragging = draggingId === p.id;
          const isTarget = dropTarget?.id === p.id;
          const dropPos = isTarget ? dropTarget.position : null;
          return (
            <button
              type="button"
              key={p.id}
              role="tab"
              aria-selected={active}
              className={`profile-bar__pill${active ? ' profile-bar__pill--active' : ''}${
                isDragging ? ' profile-bar__pill--dragging' : ''
              }${isTarget ? ` profile-bar__pill--drop-${dropPos}` : ''}`}
              draggable={canReorder}
              onDragStart={(e) => {
                if (!canReorder) return;
                e.dataTransfer.effectAllowed = 'move';
                e.dataTransfer.setData('text/plain', p.id);
                setDraggingId(p.id);
              }}
              onDragOver={(e) => {
                if (draggingId === null || draggingId === p.id) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                const rect = e.currentTarget.getBoundingClientRect();
                const midX = rect.left + rect.width / 2;
                const position: DropPosition = e.clientX < midX ? 'left' : 'right';
                if (dropTarget?.id !== p.id || dropTarget.position !== position) {
                  setDropTarget({ id: p.id, position });
                }
              }}
              onDragLeave={(e) => {
                const next = e.relatedTarget as Node | null;
                if (!e.currentTarget.contains(next)) {
                  if (dropTarget?.id === p.id) setDropTarget(null);
                }
              }}
              onDrop={(e) => {
                e.preventDefault();
                handleDrop();
              }}
              onClick={() => store.setActiveProfileId(p.id)}
              data-testid={`profile-pill-${p.id}`}
              title={canReorder ? `${p.name} (drag to reorder)` : p.name}
            >
              <span className="profile-bar__pill-name">{p.name}</span>
              {isDefault && <span className="profile-bar__pill-tag">default</span>}
            </button>
          );
        })}
      </div>

      <div className="profile-bar__actions">
        <button
          type="button"
          className="profile-bar__btn"
          onClick={() => store.addProfile()}
          disabled={!canAdd}
          title={canAdd ? 'Add a new profile' : `Max ${MAX_PROFILES} profiles`}
          data-testid="profile-bar-add"
        >
          + Add
        </button>
        <button
          type="button"
          className="profile-bar__btn"
          onClick={() => store.duplicateProfile(store.activeProfileId)}
          disabled={!canAdd}
          title="Duplicate the active profile"
          data-testid="profile-bar-duplicate"
        >
          Duplicate
        </button>
        <button
          type="button"
          className="profile-bar__btn profile-bar__btn--danger"
          onClick={handleDelete}
          disabled={!canDelete}
          title={canDelete ? 'Delete the active profile' : 'At least one profile is required'}
          data-testid="profile-bar-delete"
        >
          Delete
        </button>
      </div>
    </div>
  );
}
